import { useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, HelpCircle, Copy, Check, RefreshCw, FileText, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import Spinner from '@/components/ui/Spinner';
import {
  getAutomationIA,
  saveAutomationIA,
  setAutomationIAActive,
  getAiProviders,
  getWebhookConfig,
  saveWebhookConfig,
  rotateWebhookSecret,
  toggleWebhook,
  deleteWebhook,
  getWebhookLogs,
  removePdfFile,
} from '@/services/automationService';
import { Link } from 'react-router-dom';

type LocalPdf = {
  id: string;
  file: File;
  name: string;
  size: number; // bytes
};

const VIEW_MEDIA = `${import.meta.env.VITE_API_BASE_URL}/whatsapp/automation-ia`;

const MAX_PROMPT_CHARS = 3000;

type AiProvider = {
  id: number;
  code: string;
  name: string;
  description?: string;
  models: Array<{
    id: number;
    code: string;
    name: string;
    description?: string;
  }>;
};

type Props = { channelId?: string; onActiveChange?: (active: boolean) => void };
type AutomationIAResponse = {
  systemPrompt?: string | null;
  isActive?: boolean;
  useOwnCredentials?: boolean;
  aiProvider?: string | null;
  aiModel?: string | null;
  aiApiKey?: string | null;
  files?: Array<{
    id: number | string;
    originalName?: string | null;
    mimeType?: string | null;
    s3Key?: string | null;
    size?: number | null;
  }>;
} | null;

export default function AIChatbotSection({ channelId, onActiveChange }: Props) {
  const { t } = useTranslation();
  const [systemPrompt, setSystemPrompt] = useState<string>('');
  const [initialPrompt, setInitialPrompt] = useState<string>('');
  const [pdfs, setPdfs] = useState<LocalPdf[]>([]);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [isActive, setIsActive] = useState<boolean>(false);
  const [useOwnCredentials, setUseOwnCredentials] = useState<boolean>(false);
  const [initialUseOwnCredentials, setInitialUseOwnCredentials] = useState<boolean>(false);
  const [aiProvider, setAiProvider] = useState<string>('openai');
  const [initialAiProvider, setInitialAiProvider] = useState<string>('openai');
  const [aiModel, setAiModel] = useState<string>('gpt-4o-mini');
  const [initialAiModel, setInitialAiModel] = useState<string>('gpt-4o-mini');
  const [aiApiKey, setAiApiKey] = useState<string>('');
  const [isApiKeyMasked, setIsApiKeyMasked] = useState<boolean>(false);
  const [apiKeyEditing, setApiKeyEditing] = useState<boolean>(false);
  const [showApiKey, setShowApiKey] = useState<boolean>(false);
  const [aiProviders, setAiProviders] = useState<AiProvider[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isToggling, setIsToggling] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Webhook states
  const [webhookUrl, setWebhookUrl] = useState<string>('');
  const [webhookEnabled, setWebhookEnabled] = useState<boolean>(false);
  const [webhookSecret, setWebhookSecret] = useState<string>('');
  const [showWebhookSecret, setShowWebhookSecret] = useState<boolean>(false);
  const [copiedSecret, setCopiedSecret] = useState<boolean>(false);
  const [webhookExpanded, setWebhookExpanded] = useState<boolean>(true);
  const [webhookLogs, setWebhookLogs] = useState<any[]>([]);
  const [logsTotal, setLogsTotal] = useState<number>(0);
  const [logsLimit, setLogsLimit] = useState<number>(10);
  const [logsOffset, setLogsOffset] = useState<number>(0);
  const [loadingLogs, setLoadingLogs] = useState<boolean>(false);
  const [showLogs, setShowLogs] = useState<boolean>(false);

  const remaining = useMemo(() => MAX_PROMPT_CHARS - systemPrompt.length, [systemPrompt]);
  const promptTooLong = remaining < 0;
  const hasNewFiles = useMemo(() => pdfs.some((p) => p.file && p.file.size > 0), [pdfs]);
  const isDirty = useMemo(() =>
    systemPrompt !== initialPrompt ||
    hasNewFiles ||
    useOwnCredentials !== initialUseOwnCredentials ||
    aiProvider !== initialAiProvider ||
    aiModel !== initialAiModel ||
    apiKeyEditing,
    [systemPrompt, initialPrompt, hasNewFiles, useOwnCredentials, initialUseOwnCredentials, aiProvider, initialAiProvider, aiModel, initialAiModel, apiKeyEditing]
  );

  const availableModels = useMemo(() => {
    if (!aiProviders || aiProviders.length === 0) return [];
    const provider = aiProviders.find(p => p.code === aiProvider);
    return provider?.models || [];
  }, [aiProvider, aiProviders]);

  function onChangePrompt(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setSystemPrompt(e.target.value);
  }

  function pickFiles() {
    fileInputRef.current?.click();
  }

  function onFilesSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const next: LocalPdf[] = [];
    const rejected: string[] = [];
    // Normaliza el nombre como en el backend (base lower, espacios→guiones, ext lower)
    const normalizeName = (name: string) => {
      const i = name.lastIndexOf('.');
      // Mantener mayúsculas/minúsculas del base como en backend; solo espacios→guiones
      const base = (i > -1 ? name.slice(0, i) : name).trim().replace(/\s+/g, '-');
      const ext = (i > -1 ? name.slice(i) : '').toLowerCase();
      return `${base}${ext}`;
    };
    // Set de archivos ya persistidos (por nombre normalizado)
    const persistedNormalized = new Set(
      pdfs
        .filter((p) => p.file && p.file.size === 0)
        .map((p) => normalizeName(p.name || '')),
    );

    for (const f of files) {
      const isPdf = f.type === 'application/pdf' || /\.pdf$/i.test(f.name);
      if (!isPdf) {
        rejected.push(f.name);
        continue;
      }
      const n = normalizeName(f.name);
      if (persistedNormalized.has(n)) {
        const ok = window.confirm(
          t('fileAlreadyExistsWarning', { fileName: f.name }),
        );
        if (!ok) {
          continue;
        }
      }
      next.push({ id: `${Date.now()}-${Math.random()}`, file: f, name: f.name, size: f.size });
    }

    if (rejected.length) {
      setErrorMsg(`${t('onlyPdfsAllowed')} ${rejected.join(', ')}`);
      // limpiar mensaje de error después de unos segundos
      setTimeout(() => setErrorMsg(''), 4000);
    }

    if (next.length) {
      setPdfs((prev) => [...prev, ...next]);
    }

    // limpia el input para permitir volver a seleccionar los mismos nombres
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function removePdf(id: string) {
    // Persistido si el archivo en estado es un placeholder (size 0)
    const target = pdfs.find((p) => p.id === id);
    const isPersisted = Boolean(target && target.file.size === 0);
    if (isPersisted) {
      if (!channelId) return;
      const ok = window.confirm(t('deleteFileConfirm'));
      if (!ok) return;
      try {
        await removePdfFile(channelId, id);
      } catch (error: any) {
        const msg = error?.response?.data?.message || t('couldNotDeleteFile');
        alert(msg);
        return;
      }
    }
    setPdfs((prev) => prev.filter((p) => p.id !== id));
  }

  function prettySize(bytes: number) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  useEffect(() => {
    // Cargar proveedores de IA
    setIsLoading(true);
    getAiProviders()
      .then((data) => {
        setAiProviders(data);
      })
      .catch(() => void 0)
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    if (!channelId) return;
    setIsLoading(true);
    getAutomationIA(channelId)
      .then((data) => {
        if (data) {
          const a = data as AutomationIAResponse;
          setSystemPrompt(String(a?.systemPrompt ?? ''));
          setInitialPrompt(String(a?.systemPrompt ?? ''));
          setIsActive(Boolean(a?.isActive));
          const ownCreds = Boolean(a?.useOwnCredentials);
          setUseOwnCredentials(ownCreds);
          setInitialUseOwnCredentials(ownCreds);
          const provider = String(a?.aiProvider || 'openai');
          setAiProvider(provider);
          setInitialAiProvider(provider);
          const model = String(a?.aiModel || 'gpt-4o-mini');
          setAiModel(model);
          setInitialAiModel(model);
          const apiKey = String(a?.aiApiKey || '');
          setAiApiKey(apiKey);
          setIsApiKeyMasked(apiKey === '***');
          setApiKeyEditing(false);
          // Archivos existentes (solo lectura)
          const files = Array.isArray(a?.files)
            ? a!.files!.map((f) => ({
                id: String(f.id),
                file: new File([], f.originalName || 'archivo.pdf'),
                name: String(f.originalName || ''),
                size: Number(f.size || 0),
              }))
            : [];
          setPdfs(files);
        }
      })
      .catch(() => void 0)
      .finally(() => setIsLoading(false));
  }, [channelId]);

  useEffect(() => {
    if (!channelId) return;
    getWebhookConfig(channelId)
      .then((data) => {
        setWebhookUrl(data?.webhookUrl || '');
        setWebhookEnabled(data?.webhookEnabled || false);
        setWebhookSecret(data?.webhookSecret || '');
      })
      .catch(() => void 0);
  }, [channelId]);

  const startTour = () => {
    const driverObj = driver({
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      steps: [
        {
          element: '#ai-chatbot-header',
          popover: {
            title: t('aiChatbotTourTitle'),
            description: t('aiChatbotTourDescription'),
            side: 'bottom',
            align: 'start'
          }
        },
        {
          element: '#system-prompt-section',
          popover: {
            title: t('tourSystemPromptTitle'),
            description: t('tourSystemPromptDescription'),
            side: 'bottom',
            align: 'start'
          }
        },
        {
          element: '#use-own-credentials-section',
          popover: {
            title: t('tourUseOwnCredentialsTitle'),
            description: t('tourUseOwnCredentialsDescription'),
            side: 'left',
            align: 'start'
          }
        },
        {
          element: '#documents-section',
          popover: {
            title: t('tourDocumentsTitle'),
            description: t('tourDocumentsDescription'),
            side: 'top',
            align: 'start'
          }
        },
        {
          element: '#save-button',
          popover: {
            title: t('tourSaveButtonTitle'),
            description: t('tourSaveButtonDescription'),
            side: 'top',
            align: 'start'
          }
        },
        {
          element: '#activate-button',
          popover: {
            title: t('tourActivateButtonTitle'),
            description: t('tourActivateButtonDescription'),
            side: 'top',
            align: 'start'
          }
        }
      ]
    });
    
    driverObj.drive();
  };

  // Webhook handlers
  const handleSaveWebhook = async () => {
    if (!channelId) return;
    if (!webhookUrl) {
      alert(t('webhookUrlRequired') || 'Webhook URL is required');
      return;
    }
    if (!webhookUrl.startsWith('https://')) {
      alert(t('webhookMustBeHttps') || 'Webhook URL must use HTTPS');
      return;
    }

    try {
      const result = await saveWebhookConfig(channelId, webhookUrl);
      setWebhookSecret(result.webhookSecret);
      setWebhookEnabled(result.webhookEnabled);
      setShowWebhookSecret(true);
      alert(t('webhookSaved') || 'Webhook saved! Copy the secret below.');
    } catch (e: any) {
      alert(e?.response?.data?.message || t('errorSavingWebhook') || 'Error saving webhook');
    }
  };

  const handleRotateSecret = async () => {
    if (!channelId) return;
    const confirmed = window.confirm(
      t('confirmRotateSecret') || 'Rotating the secret will invalidate the old one. Continue?'
    );
    if (!confirmed) return;

    try {
      const result = await rotateWebhookSecret(channelId);
      setWebhookSecret(result.webhookSecret);
      setShowWebhookSecret(true);
      alert(t('secretRotated') || 'Secret rotated! Copy the new secret below.');
    } catch (e: any) {
      alert(e?.response?.data?.message || t('errorRotatingSecret') || 'Error rotating secret');
    }
  };

  const handleToggleWebhook = async () => {
    if (!channelId) return;
    try {
      const result = await toggleWebhook(channelId, !webhookEnabled);
      setWebhookEnabled(result.webhookEnabled);
    } catch (e: any) {
      alert(e?.response?.data?.message || t('errorTogglingWebhook') || 'Error toggling webhook');
    }
  };

  const handleDeleteWebhook = async () => {
    if (!channelId) return;
    const confirmed = window.confirm(
      t('confirmDeleteWebhook') || 'Are you sure you want to delete the webhook configuration?'
    );
    if (!confirmed) return;

    try {
      await deleteWebhook(channelId);
      setWebhookUrl('');
      setWebhookSecret('');
      setWebhookEnabled(false);
      alert(t('webhookDeleted') || 'Webhook deleted');
    } catch (e: any) {
      alert(e?.response?.data?.message || t('errorDeletingWebhook') || 'Error deleting webhook');
    }
  };

  const handleCopySecret = () => {
    if (webhookSecret && webhookSecret !== '***') {
      navigator.clipboard.writeText(webhookSecret);
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    }
  };

  const handleLoadLogs = async () => {
    if (!channelId) return;
    setLoadingLogs(true);
    try {
      const result = await getWebhookLogs(channelId, logsLimit, logsOffset);
      setWebhookLogs(result.logs);
      setLogsTotal(result.total);
      setShowLogs(true);
    } catch (e: any) {
      alert(e?.response?.data?.message || t('errorLoadingLogs') || 'Error loading logs');
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleNextPage = () => {
    const newOffset = logsOffset + logsLimit;
    if (newOffset < logsTotal) {
      setLogsOffset(newOffset);
    }
  };

  const handlePrevPage = () => {
    const newOffset = Math.max(0, logsOffset - logsLimit);
    setLogsOffset(newOffset);
  };

  useEffect(() => {
    if (showLogs && channelId) {
      handleLoadLogs();
    }
  }, [logsOffset, logsLimit]);

  return (
    <div className="p-6 w-full h-[calc(100vh-100px)] overflow-auto">
      {(isLoading || isSaving || isToggling) && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="">
            <Spinner size="lg" color="primary" />
          </div>
        </div>
      )}
      <div className="max-w-4xl space-y-6">
        <div id="ai-chatbot-header" className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-semibold">{t('aiChatbot')}</h2>
            <p className="text-gray-600">{t('aiChatbotDescription')}</p>
          </div>
          <button
            type="button"
            onClick={startTour}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
            title={t('startTour')}
          >
            <HelpCircle size={20} />
          </button>
        </div>

        {/* System Prompt */}
        <section id="system-prompt-section" className="space-y-2">
          <label className="block text-sm font-medium text-gray-800" htmlFor="system-prompt">
            {t('systemPrompt')}
          </label>
          <textarea
            id="system-prompt"
            value={systemPrompt}
            onChange={onChangePrompt}
            className={`w-full h-40 resize-y rounded-md border px-3 py-2 outline-none ${promptTooLong ? 'border-red-500 focus:ring-2 focus:ring-red-300' : 'border-gray-300 focus:ring-2 focus:ring-green-200'}`}
            placeholder={t('systemPromptPlaceholder')}
          />
          <div className="flex items-center justify-between text-sm">
            <span className={promptTooLong ? 'text-red-600' : 'text-gray-500'}>
              {promptTooLong ? t('exceededCharLimit') : t('promptTip')}
            </span>
            <span className={promptTooLong ? 'text-red-600' : 'text-gray-500'}>
              {Math.max(0, systemPrompt.length)} / {MAX_PROMPT_CHARS}
            </span>
          </div>
        </section>

        {/* Usar credenciales propias */}
        <section id="use-own-credentials-section" className="space-y-3 border-t pt-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="use-own-credentials"
              checked={useOwnCredentials}
              onChange={(e) => setUseOwnCredentials(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
            />
            <label htmlFor="use-own-credentials" className="text-sm font-medium text-gray-800">
              {t('useOwnCredentials')}
            </label>
          </div>
          <p className="text-xs text-gray-500 ml-7">
            {t('useOwnCredentialsDescription')}
          </p>

          {useOwnCredentials && (
            <div className="ml-7 space-y-4 p-4 bg-gray-50 rounded-md border border-gray-200">
              {/* Provider */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700" htmlFor="ai-provider">
                  {t('provider')}
                </label>
                <select
                  id="ai-provider"
                  value={aiProvider}
                  onChange={(e) => {
                    setAiProvider(e.target.value);
                    // Reset model when provider changes
                    const newProvider = aiProviders.find(p => p.code === e.target.value);
                    if (newProvider && newProvider.models.length > 0) {
                      setAiModel(newProvider.models[0].code);
                    }
                  }}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-green-200 outline-none"
                >
                  {aiProviders && aiProviders.length > 0 && aiProviders.map((p) => (
                    <option key={p.code} value={p.code}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Model */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700" htmlFor="ai-model">
                  {t('model')}
                </label>
                <select
                  id="ai-model"
                  value={aiModel}
                  onChange={(e) => setAiModel(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-green-200 outline-none"
                >
                  {availableModels && availableModels.length > 0 && availableModels.map((model) => (
                    <option key={model.code} value={model.code}>
                      {model.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* API Key */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700" htmlFor="ai-api-key">
                  {t('apiKey')}
                </label>
                {isApiKeyMasked && !apiKeyEditing ? (
                  <div className="flex items-center gap-2">
                    <input
                      id="ai-api-key"
                      type="password"
                      value={aiApiKey}
                      readOnly
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-gray-100 font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setApiKeyEditing(true);
                        setAiApiKey('');
                      }}
                      className="whitespace-nowrap px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded"
                    >
                      {t('changeApiKey')}
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      id="ai-api-key"
                      type={showApiKey ? 'text' : 'password'}
                      value={aiApiKey}
                      onChange={(e) => {
                        setAiApiKey(e.target.value);
                        setApiKeyEditing(true);
                      }}
                      placeholder="sk-..."
                      className="w-full rounded-md border border-gray-300 px-3 py-2 pr-10 text-sm focus:ring-2 focus:ring-green-200 outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showApiKey ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  {t('apiKeyStoredSecurely')}
                </p>
              </div>
            </div>
          )}
        </section>

        {/* Carga de PDFs */}
        <section id="documents-section" className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-800">{t('documents')}</label>
            <button
              type="button"
              onClick={pickFiles}
              className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-green-100 border border-gray-300 rounded"
            >
              {t('addFiles')}
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            multiple
            className="hidden"
            onChange={onFilesSelected}
          />

          {errorMsg && <div className="text-sm text-red-600">{errorMsg}</div>}

          <div className="rounded border border-dashed border-gray-300 p-4 bg-white">
            {pdfs.length === 0 ? (
              <p className="text-sm text-gray-500">{t('noFilesLoaded')}</p>
            ) : (
              <ul className="divide-y">
                {pdfs.map((p) => (
                  <li key={p.id} className="py-2 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-800 truncate">{p.name}</p>
                      <p className="text-xs text-gray-500">{prettySize(p.size)}</p>
                    </div>
                    {/* Si es un archivo ya persistido, mostrar acciones */}
                    {typeof p.id === 'string' && p.file.size > 0 ? null : (
                      <div className="flex items-center gap-2">
                        <a
                          href={`${VIEW_MEDIA}/${channelId}/files/${p.id}/view`}
                          target="_blank"
                          rel="noreferrer"
                          className="px-2 py-1 text-xs bg-gray-100 border border-gray-300 rounded hover:bg-green-100"
                        >
                          {t('view')}
                        </a>
                        <a
                          href={`${VIEW_MEDIA}/${channelId}/files/${p.id}/download`}
                          className="px-2 py-1 text-xs bg-gray-100 border border-gray-300 rounded hover:bg-green-100"
                        >
                          {t('download')}
                        </a>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removePdf(p.id)}
                      className="ml-2 px-2 py-1 text-xs bg-red-50 text-red-600 border border-red-200 rounded hover:bg-red-100"
                    >
                      {t('remove')}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* Acciones */}
        <div className="pt-2 flex items-center gap-3">
          <button
            id="save-button"
            type="button"
    disabled={promptTooLong || !channelId || !isDirty || isSaving}
    className={`px-4 py-2 rounded text-white flex items-center gap-2 ${promptTooLong || !channelId || !isDirty || isSaving ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
            onClick={async () => {
              if (!channelId) return;
              setIsSaving(true);
              try {
                const newFiles = pdfs.filter((p) => p.file && p.file.size > 0).map((p) => p.file);
                await saveAutomationIA(channelId, {
                  systemPrompt,
                  files: newFiles,
                  useOwnCredentials,
                  aiProvider: useOwnCredentials ? aiProvider : undefined,
                  aiModel: useOwnCredentials ? aiModel : undefined,
                  aiApiKey: useOwnCredentials && apiKeyEditing && aiApiKey ? aiApiKey : undefined,
                });
                // Tras guardar, refrescar desde backend para que los archivos queden como persistidos (size 0)
                try {
                  const latest = (await getAutomationIA(channelId)) as AutomationIAResponse;
                  if (latest) {
                    setIsActive(Boolean(latest?.isActive));
                    const ownCreds = Boolean(latest?.useOwnCredentials);
                    setUseOwnCredentials(ownCreds);
                    setInitialUseOwnCredentials(ownCreds);
                    const provider = String(latest?.aiProvider || 'openai');
                    setAiProvider(provider);
                    setInitialAiProvider(provider);
                    const model = String(latest?.aiModel || 'gpt-4o-mini');
                    setAiModel(model);
                    setInitialAiModel(model);
                    const apiKey = String(latest?.aiApiKey || '');
                    setAiApiKey(apiKey);
                    setIsApiKeyMasked(apiKey === '***');
                    setApiKeyEditing(false);
                    const files = Array.isArray(latest?.files)
                      ? latest!.files!.map((f) => ({
                          id: String(f.id),
                          file: new File([], f.originalName || 'archivo.pdf'),
                          name: String(f.originalName || ''),
                          size: Number(f.size || 0),
                        }))
                      : [];
                    setPdfs(files);
                    setInitialPrompt(String(latest?.systemPrompt ?? ''));
                  } else {
                    setPdfs([]);
                  }
                } catch {
                  // Si falla el refresh, al menos elimina los que eran nuevos para no re-subirlos en el próximo guardado
                  setPdfs((prev) => prev.filter((p) => p.file.size === 0));
                }
                alert(t('saved'));
              } finally {
                setIsSaving(false);
              }
            }}
          >
            {isSaving && <Spinner size="sm" color="white" />}
            {t('save')}
          </button>

          <button
            id="activate-button"
            type="button"
            disabled={!channelId || promptTooLong || (!systemPrompt && pdfs.length === 0) || isToggling}
            onClick={async () => {
              if (!channelId) return;
              const next = !isActive;
              setIsToggling(true);
              try {
                const result = (await setAutomationIAActive(channelId, next)) as unknown as {
                  isActive?: boolean;
                };
                const newVal = Boolean(result?.isActive ?? next);
                setIsActive(newVal);
                onActiveChange?.(newVal);
              } catch (e) {
                const err = e as { response?: { data?: { message?: string } } };
                const msg: string = err?.response?.data?.message || t('couldNotChangeStatus');
                alert(msg);
              } finally {
                setIsToggling(false);
              }
            }}
            className={`px-4 py-2 rounded flex items-center gap-2 ${!channelId || promptTooLong || (!systemPrompt && pdfs.length === 0) || isToggling ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : isActive ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
          >
            {isToggling && <Spinner size="sm" color="white" />}
            {isActive ? t('deactivateAiBot') : t('activateAiBot')}
          </button>
        </div>

        {/* Webhook Configuration Section */}
        <section id="webhook-section" className="border-t pt-6 mt-6">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setWebhookExpanded(!webhookExpanded)}
          >
            <div>
              <h3 className="text-lg font-semibold text-gray-800">
                {t('webhookConfiguration') || 'Webhook Configuration'}
              </h3>
              <p className="text-sm text-gray-600">
                {t('webhookDescription') || 'Advanced: Configure webhook for external actions'}
              </p>
            </div>
            {webhookExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>

          {webhookExpanded && (
            <div className="mt-4 space-y-4">
              {/* Webhook URL */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700" htmlFor="webhook-url">
                  {t('webhookUrl') || 'Webhook URL'}
                </label>
                <input
                  id="webhook-url"
                  type="url"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="https://your-server.com/webhook.php"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-green-200 outline-none"
                />
                <p className="text-xs text-gray-500">
                  {t('webhookUrlHint') || 'Must be HTTPS. Actions detected by AI will be sent here.'}
                </p>
              </div>

              {/* Webhook Secret */}
              {webhookSecret && webhookSecret !== '' && (
                <div className="space-y-2 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700">
                      {t('webhookSecret') || 'Webhook Secret'}
                    </label>
                    {webhookSecret !== '***' && (
                      <button
                        type="button"
                        onClick={handleCopySecret}
                        className="flex items-center gap-1 px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50"
                      >
                        {copiedSecret ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
                        {copiedSecret ? (t('copied') || 'Copied!') : (t('copy') || 'Copy')}
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <input
                      type={showWebhookSecret ? 'text' : 'password'}
                      value={webhookSecret}
                      readOnly
                      className="w-full rounded-md border border-gray-300 px-3 py-2 pr-10 text-sm bg-white font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showWebhookSecret ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {webhookSecret !== '***' && (
                    <p className="text-xs text-yellow-700 font-medium">
                      ⚠️ {t('webhookSecretWarning') || 'Save this secret! It will be hidden after this session.'}
                    </p>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap items-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleSaveWebhook}
                  disabled={!channelId || !webhookUrl}
                  className={`px-4 py-2 text-sm rounded ${!channelId || !webhookUrl ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-700'}`}
                >
                  {t('saveWebhook') || 'Save Webhook'}
                </button>

                {webhookSecret && webhookSecret !== '' && (
                  <>
                    <button
                      type="button"
                      onClick={handleRotateSecret}
                      disabled={!channelId}
                      className="flex items-center gap-1 px-4 py-2 text-sm bg-orange-600 text-white rounded hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      <RefreshCw size={14} />
                      {t('rotateSecret') || 'Rotate Secret'}
                    </button>

                    <button
                      type="button"
                      onClick={handleToggleWebhook}
                      disabled={!channelId}
                      className={`px-4 py-2 text-sm rounded ${webhookEnabled ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-blue-600 text-white hover:bg-blue-700'} disabled:bg-gray-300 disabled:cursor-not-allowed`}
                    >
                      {webhookEnabled ? (t('disableWebhook') || 'Disable') : (t('enableWebhook') || 'Enable')}
                    </button>

                    <button
                      type="button"
                      onClick={handleLoadLogs}
                      disabled={!channelId || loadingLogs}
                      className="flex items-center gap-1 px-4 py-2 text-sm bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 disabled:bg-gray-200 disabled:cursor-not-allowed"
                    >
                      <FileText size={14} />
                      {loadingLogs ? (t('loading') || 'Loading...') : (t('viewLogs') || 'View Logs')}
                    </button>

                    <button
                      type="button"
                      onClick={handleDeleteWebhook}
                      disabled={!channelId}
                      className="px-4 py-2 text-sm bg-red-50 text-red-600 border border-red-200 rounded hover:bg-red-100 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      {t('deleteWebhook') || 'Delete Webhook'}
                    </button>
                  </>
                )}
              </div>

              {/* Status Indicator */}
              {webhookSecret && (
                <div className="flex items-center gap-2 text-sm">
                  <span className={`w-2 h-2 rounded-full ${webhookEnabled ? 'bg-green-500' : 'bg-gray-400'}`} />
                  <span className="text-gray-700">
                    {webhookEnabled ? (t('webhookActive') || 'Webhook Active') : (t('webhookInactive') || 'Webhook Inactive')}
                  </span>
                </div>
              )}

              {/* Logs Table */}
              {showLogs && (
                <div className="mt-4 border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-800">
                      {t('webhookLogs') || 'Webhook Execution Logs'} ({logsTotal})
                    </h4>
                    <button
                      type="button"
                      onClick={() => setShowLogs(false)}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      {t('hide') || 'Hide'}
                    </button>
                  </div>

                  {webhookLogs.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">
                      {t('noLogsYet') || 'No logs yet'}
                    </p>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border-collapse">
                          <thead>
                            <tr className="bg-gray-50 border-b">
                              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('date') || 'Date'}</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('status') || 'Status'}</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('duration') || 'Duration'}</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('actions') || 'Actions'}</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('error') || 'Error'}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {webhookLogs.map((log) => (
                              <tr key={log.id} className="border-b hover:bg-gray-50">
                                <td className="px-3 py-2 text-gray-600">
                                  {new Date(log.executedAt).toLocaleString()}
                                </td>
                                <td className="px-3 py-2">
                                  <span
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                                      log.success
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-red-100 text-red-700'
                                    }`}
                                  >
                                    {log.success ? '✓' : '✗'}
                                    {log.httpStatusCode || 'N/A'}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-gray-600">
                                  {log.durationMs}ms
                                </td>
                                <td className="px-3 py-2 text-gray-600">
                                  {log.requestPayload?.actions?.length || 0} action(s)
                                  {log.requestPayload?.actions?.length > 0 && (
                                    <div className="text-xs text-gray-500 mt-1">
                                      {log.requestPayload.actions.map((a: any, i: number) => (
                                        <span key={i} className="inline-block mr-1">
                                          {a.action}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-red-600 text-xs">
                                  {log.error || '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      <div className="flex items-center justify-between mt-3 text-xs">
                        <div className="text-gray-600">
                          {t('showing') || 'Showing'} {logsOffset + 1}-{Math.min(logsOffset + logsLimit, logsTotal)} {t('of') || 'of'} {logsTotal}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={handlePrevPage}
                            disabled={logsOffset === 0}
                            className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          >
                            {t('previous') || 'Previous'}
                          </button>
                          <button
                            type="button"
                            onClick={handleNextPage}
                            disabled={logsOffset + logsLimit >= logsTotal}
                            className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          >
                            {t('next') || 'Next'}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

        </section>


        <Link
          to="/ai-chatbot-documentation"
          className="inline-flex items-center text-sm text-green-600 hover:text-green-700 font-medium"
        >
          {t('aiChatbotDocumentation')}
          <ExternalLink className="ml-1 h-4 w-4" />
        </Link>
      </div>

      
    </div>
  );
}
